import type { ApiError } from '../shared/errors';
import type { DomainQuery } from '../shared/query';

/**
 * The fish group a pen's images are scoped to, and the state of the lookup that finds it.
 *
 * Until `isResolved`, the images are not requested: without a group there is no way to tell the
 * group's images from those of the groups the pen held before. A failed lookup is not an empty
 * pen, so it does not fall back to the whole cage either; its `error` stands for the images',
 * and retrying the images retries the lookup.
 */
export interface ImagesFishGroupScope {
    /** `undefined` once resolved means the whole cage: flag off, or an empty pen. */
    fishGroupId?: number;
    isResolved: boolean;
    error: ApiError | null;
    isFetching: boolean;
    refetch: () => unknown;
}

/** Whether the images request may run under `scope`. No scope, no restriction. */
export function isScopeReady(scope: ImagesFishGroupScope | undefined): boolean {
    return scope == null || (scope.isResolved && scope.error == null);
}

/**
 * The images query as the page may see it under `scope`. Until the scope is ready the request's
 * key is the cage-wide one, and a disabled query still returns what that key has cached, the
 * other groups' images included; so nothing of it is shown. `whileWaiting` stands in for the
 * data meanwhile (the empty list for a caller that never shows a loading state). A failed lookup
 * is the images' error, and retrying them retries it.
 */
export function withFishGroupScope<T>(
    images: DomainQuery<T>,
    scope: ImagesFishGroupScope | undefined,
    whileWaiting?: T
): DomainQuery<T> {
    if (scope == null || isScopeReady(scope)) {
        return images;
    }
    const waiting = {
        data: whileWaiting,
        isFetched: false,
        isSuccess: false,
    };
    if (scope.error != null) {
        return {
            ...waiting,
            isLoading: false,
            isFetching: scope.isFetching,
            isError: true,
            error: scope.error,
            refetch: async () => scope.refetch(),
        };
    }
    return {
        ...waiting,
        isLoading: whileWaiting === undefined,
        isFetching: false,
        isError: false,
        error: null,
        refetch: images.refetch,
    };
}
